import { API } from "../api";
import { url } from "../constants";
import {
  ICancelSubscriptionRequest,
  ICancelSubscriptionResponse,
  ICreateSubscriptionRequest,
  IRenewalInvoiceStatus,
  ISubscription,
  ISubscriptionDetails,
  ISubscriptionOptions,
  ISubscriptionPreview,
  ISubscriptionRequest,
  ISubscriptionsResponse,
  IUpdateSubscriptionRequest,
  IUpdateSubscriptionResponse,
} from "./types";
import { IAPIRequestConfig } from "../types";
import { Invoice } from "../../../types/common";
import { getETagCode, validateArgs } from "../utils";

export class SubscriptionAPIService {
  constructor(private api: API) {}

  async getAll(customerId: string, params?: any): Promise<ISubscription[]> {
    validateArgs(customerId);
    const response = await this.api.get<ISubscriptionsResponse>(
      url.subscriptions.get.replace(":customerId", customerId),
      { params },
    );

    return response?.data?.items ?? [];
  }

  async getDetail(
    customerId: string,
    subscriptionId: string,
  ): Promise<ISubscriptionDetails> {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.get<ISubscriptionDetails>(
      url.subscriptions.getDetail
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
    );
    return response?.data as ISubscriptionDetails;
  }

  async previewSubscription(
    customerId: string,
    spData: ICreateSubscriptionRequest,
  ) {
    validateArgs(customerId);
    const response = await this.api.post<ISubscriptionPreview>(
      url.subscriptions.previewSubscription.replace(":customerId", customerId),
      spData,
    );
    return response?.data as ISubscriptionPreview;
  }

  async previewUpdateSubscription(
    customerId: string,
    subscriptionId: string,
    spData: IUpdateSubscriptionRequest,
  ) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post<ISubscriptionPreview>(
      url.subscriptions.previewUpdate
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      spData,
    );

    return response?.data as ISubscriptionPreview;
  }

  async create(
    subscriptionData: ICreateSubscriptionRequest,
    customerId: string,
    config?: IAPIRequestConfig,
  ) {
    validateArgs(customerId);
    const response = await this.api.post<ISubscriptionsResponse>(
      url.subscriptions.create.replace(":customerId", customerId),
      subscriptionData,
      config,
    );
    return response?.data as ISubscriptionsResponse;
  }

  async updateSubscription(
    customerId: string,
    subscriptionId: string,
    spData: IUpdateSubscriptionRequest,
  ) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post<IUpdateSubscriptionResponse>(
      url.subscriptions.update
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      spData,
    );
    return response?.data as IUpdateSubscriptionResponse;
  }

  async cancel({
    customerId,
    cancelReason,
    subscriptionId,
  }: ICancelSubscriptionRequest) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post<ICancelSubscriptionResponse>(
      url.subscriptions.cancel
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      { cancel_reason: cancelReason },
    );

    return response?.data as ICancelSubscriptionResponse;
  }

  async uncancel({ customerId, subscriptionId }: ISubscriptionRequest) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post(
      url.subscriptions.uncancel
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return response?.data;
  }

  async pause({ customerId, subscriptionId }: ISubscriptionRequest) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post(
      url.subscriptions.pause
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return response?.data;
  }

  async unpause({ customerId, subscriptionId }: ISubscriptionRequest) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post(
      url.subscriptions.unpause
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return response?.data;
  }

  async recycle(customerId: string, subscriptionId: string) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post(
      url.subscriptions.recycle
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return response?.data;
  }

  // PH_SUBSCRIPTION renewals are billed on demand: the portal asks whether the
  // next renewal is payable, and only mints an invoice once the customer asks
  // to pay one. Other providers answer NotSupported.
  async getRenewalInvoiceStatus({
    customerId,
    subscriptionId,
  }: ISubscriptionRequest): Promise<IRenewalInvoiceStatus> {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.get<IRenewalInvoiceStatus>(
      url.subscriptions.renewalInvoice
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
    );

    return response?.data as IRenewalInvoiceStatus;
  }

  // Idempotent server-side: returns the live invoice for the current period when
  // there is one, and mints a fresh one otherwise. A renewal that nets to zero
  // comes back already paid, with an empty sp_customer_invoice_uri.
  async createRenewalInvoice({
    customerId,
    subscriptionId,
  }: ISubscriptionRequest): Promise<Invoice> {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.post<Invoice>(
      url.subscriptions.renewalInvoice
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return response?.data as Invoice;
  }

  async options(customerId: string, subscriptionId: string) {
    validateArgs(customerId, subscriptionId);
    const response = await this.api.get<ISubscriptionOptions>(
      url.subscriptions.options
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {},
    );

    return {
      ...(response?.data ?? {}),
      eTag: response?.headers?.etag,
    };
  }

  async editOptions(
    customerId: string,
    subscriptionId: string,
    autoRecycle: boolean,
    ifMatchOption: string,
  ) {
    validateArgs(customerId, subscriptionId);
    const etag = getETagCode(ifMatchOption);

    if (etag) {
      this.api.addHeader("If-Match", etag);
    } else {
      this.api.addHeader("If-None-Match", "*");
    }

    const response = await this.api.put<ISubscriptionOptions>(
      url.subscriptions.options
        .replace(":customerId", customerId)
        .replace(":subscriptionId", subscriptionId),
      {
        auto_recycle: autoRecycle,
      },
    );
    if (etag) {
      this.api.removeHeader("If-Match");
    } else {
      this.api.removeHeader("If-None-Match");
    }

    return response?.data as ISubscriptionOptions;
  }
}
